import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { financeService } from '../services/financeService';
import { analyticsService } from '../services/analyticsService';
import { rbacService } from '../services/rbacService';
import { withCache } from '../services/cacheService';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  DateRange as DateRangeIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  DonutSmall as DonutSmallIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  BuildCircle as BuildCircleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

const InventoryAnalytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, hasPermission } = useAuth();
  const { showNotification, showLoading } = useNotification();

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Estados de dados
  const [inventoryData, setInventoryData] = useState({
    summary: null,
    assets: [],
    statusDistribution: [],
    categoryBreakdown: [],
    valueAnalysis: [],
    preparationCosts: [],
    timeline: []
  });
  
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [productFilterLabel, setProductFilterLabel] = useState('');
  const [enums, setEnums] = useState({
    assetStatuses: []
  });

  // Estados de filtros
  const [filters, setFilters] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    status: '',
    category: '',
    productId: '',
    minValue: '',
    maxValue: '',
    managedBy: '',
    assetId: ''
  });

  const [loadingAssets, setLoadingAssets] = useState(false);

  // Cores para gráficos e status - Paleta harmoniosa e contrastante
  const chartColors = [
    '#1976d2', // Azul
    '#4caf50', // Verde
    '#ff9800', // Laranja
    '#f44336', // Vermelho
    '#9c27b0', // Roxo
    '#00bcd4', // Ciano
    '#795548', // Marrom
    '#607d8b', // Azul acinzentado
    '#e91e63', // Rosa
    '#8bc34a', // Verde claro
    '#ffc107', // Âmbar
    '#673ab7'  // Roxo escuro
  ];

  const statusColors = {
    'em_aquisicao': '#2196f3',        // Azul - Em Aquisição
    'em_preparacao': '#ff9800',       // Laranja - Em Preparação  
    'pronto_sorteio': '#4caf50',      // Verde - Pronto para Sorteio
    'sorteado_aguardando': '#9c27b0', // Roxo - Sorteado (Aguardando)
    'entregue': '#9e9e9e',            // Cinza - Entregue
    'cancelado': '#f44336',           // Vermelho - Cancelado
    // Mapeamentos alternativos para compatibilidade
    'planejado': '#2196f3',
    'preparado': '#4caf50'
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (Object.values(filters).some(f => f !== '')) {
      loadInventoryData();
    }
  }, [filters]);

  // Keep label in sync for Product/Sorteio filter
  useEffect(() => {
    const id = String(filters.productId ?? '').trim();
    if (!id) {
      if (productFilterLabel) setProductFilterLabel('');
      return;
    }
    const match = (products || []).find(p => String(p.id ?? '').trim() === id);
    if (match?.title && match.title !== productFilterLabel) {
      setProductFilterLabel(match.title);
    }
  }, [filters.productId, products, productFilterLabel]);

  // Lazy-load assets when user opens tabs that require detailed data.
  // Also re-run when summary changes (e.g., after filter updates) so tabs don't go blank.
  useEffect(() => {
    if (currentTab !== 0 && inventoryData.summary) {
      ensureAssetsLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, inventoryData.summary]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Carregar apenas dados essenciais de forma sequencial e otimizada
      await Promise.all([
        loadEnums(),
        loadProducts()
      ]);
      // Dados de usuários e inventory carregam sob demanda
      await loadInventoryData();
    } catch (err) {
      setError(err.message);
      showNotification('Erro ao carregar dados iniciais', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadEnums = async () => {
    try {
      // Cache de 1 hora para enums
      const statuses = await withCache(
        'inventory_asset_statuses',
        () => financeService.getAssetStatuses(),
        60 * 60 * 1000
      );
      setEnums({
        assetStatuses: statuses || []
      });
    } catch (err) {
      console.error('Erro ao carregar enums:', err);
    }
  };

  const loadProducts = async () => {
    try {
      // Carrega produtos apenas se possuir permissão
      if (!hasPermission || !hasPermission('finance.products.read')) {
        setProducts([]);
        return;
      }
      // Cache de 10 minutos para produtos
      const productsData = await withCache(
        'inventory_products_list',
        () => financeService.getAvailableProducts(),
        10 * 60 * 1000
      );
      setProducts(productsData || []);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    }
  };

  const loadUsers = async () => {
    // Carregar usuários apenas quando o filtro for aberto
    if (users.length > 0) return; // Já carregado
    
    try {
      // Cache de 10 minutos para usuários
      const usersData = await withCache(
        'inventory_users_list',
        () => rbacService.getUsers(),
        10 * 60 * 1000
      );
      setUsers(usersData || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    }
  };

  // Assets são buscados apenas via AsyncSearchSelect sob demanda

  // Helper functions for processing data
  const processStatusDistribution = (assets) => {
    const distribution = {};
    assets.forEach(asset => {
      const status = asset.status || 'unknown';
      if (!distribution[status]) {
        distribution[status] = { 
          status, 
          name: getStatusLabel(status),
          count: 0, 
          totalValue: 0,
          value: 0,
          color: statusColors[status] || '#9e9e9e'
        };
      }
      distribution[status].count += 1;
      const assetValue = parseFloat(asset.final_value || asset.initial_value || 0);
      distribution[status].totalValue += assetValue;
      distribution[status].value = distribution[status].totalValue; // Para compatibilidade
    });
    return Object.values(distribution);
  };

  const processCategoryBreakdown = (assets) => {
    const breakdown = {};
    let colorIndex = 0;
    
    assets.forEach((asset) => {
      const category = asset.category || 'Outros';
      if (!breakdown[category]) {
        breakdown[category] = { 
          category, 
          name: category, // Para compatibilidade com o gráfico
          count: 0, 
          totalValue: 0, 
          avgValue: 0,
          color: chartColors[colorIndex % chartColors.length]
        };
        colorIndex++;
      }
      breakdown[category].count += 1;
      breakdown[category].totalValue += parseFloat(asset.final_value || asset.initial_value || 0);
    });
    
    // Calcular média e ordenar por valor total (decrescente)
    const result = Object.values(breakdown);
    result.forEach(item => {
      item.avgValue = item.count > 0 ? item.totalValue / item.count : 0;
    });
    
    // Ordenar por valor total para melhor visualização
    return result.sort((a, b) => b.totalValue - a.totalValue);
  };

  const processValueAnalysis = (assets) => {
    const ranges = [
      { label: 'Até R$ 10k', min: 0, max: 10000, color: '#4caf50' },        // Verde - Valor baixo
      { label: 'R$ 10k - R$ 50k', min: 10000, max: 50000, color: '#2196f3' },   // Azul - Valor médio-baixo  
      { label: 'R$ 50k - R$ 100k', min: 50000, max: 100000, color: '#ff9800' }, // Laranja - Valor médio-alto
      { label: 'Acima de R$ 100k', min: 100000, max: Infinity, color: '#f44336' } // Vermelho - Valor alto
    ];
    
    return ranges.map(range => {
      const assetsInRange = assets.filter(asset => {
        const value = parseFloat(asset.final_value || 0);
        return value >= range.min && value < range.max;
      });
      
      return {
        ...range,
        count: assetsInRange.length,
        totalValue: assetsInRange.reduce((sum, asset) => sum + parseFloat(asset.final_value || 0), 0)
      };
    });
  };

  // Agrega dados de ativos em estruturas para gráficos do inventário
  const processInventoryData = (assets, summary) => {
    const statusDistribution = processStatusDistribution(assets);
    const categoryBreakdown = processCategoryBreakdown(assets);
    const valueAnalysis = processValueAnalysis(assets);
    const preparationCosts = processPreparationCosts(assets);
    const timeline = processTimeline(assets);
    return {
      statusDistribution,
      categoryBreakdown,
      valueAnalysis,
      preparationCosts,
      timeline
    };
  };
  
  // Processar dados diretamente do summary (mais eficiente)
  const processInventoryDataFromSummary = (summary) => {
    if (!summary) return {
      statusDistribution: [],
      categoryBreakdown: [],
      valueAnalysis: [],
      preparationCosts: [],
      timeline: []
    };
    
    // Status distribution já vem no summary
    const statusDistribution = Object.entries(summary.assets_by_status || {}).map(([status, count]) => ({
      status,
      name: getStatusLabel(status),
      count,
      totalValue: 0, // Summary não tem valor por status individual
      value: 0,
      color: statusColors[status] || '#9e9e9e'
    }));
    
    return {
      statusDistribution,
      categoryBreakdown: [], // Requer assets individuais
      valueAnalysis: [], // Requer assets individuais
      preparationCosts: [], // Pode ser calculado do summary
      timeline: [] // Requer assets individuais
    };
  };

  const loadInventoryData = async () => {
    try {
      const params = {
        start_date: filters.startDate ? new Date(filters.startDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
        end_date: filters.endDate ? new Date(filters.endDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
        status: filters.status || undefined,
        category: filters.category || undefined,
        product_id: filters.productId || undefined,
        min_value: filters.minValue ? parseFloat(filters.minValue) : undefined,
        max_value: filters.maxValue ? parseFloat(filters.maxValue) : undefined,
        managed_by: filters.managedBy || undefined,
        asset_id: filters.assetId || undefined
      };

      // Uma única chamada com cache de 5 minutos
      const summary = await withCache(
        `inventory_summary_${JSON.stringify(params)}`,
        () => financeService.getAssetSummary(params),
        5 * 60 * 1000
      );

      // Por padrão: preencher apenas Status via summary (rápido).
      // Outras abas carregam assets sob demanda (lazy) para manter performance.
      const processedData = processInventoryDataFromSummary(summary);
      setInventoryData(prev => ({
        ...prev,
        summary,
        assets: [],
        ...processedData
      }));
    } catch (err) {
      console.error('Erro ao carregar dados do inventário:', err);
      showNotification('Erro ao carregar análises do inventário', 'error');
    }
  };

  const ensureAssetsLoaded = async () => {
    if (!inventoryData.summary) return null;

    if (inventoryData.assets && inventoryData.assets.length > 0) {
      return {
        assets: inventoryData.assets,
        statusDistribution: inventoryData.statusDistribution,
        categoryBreakdown: inventoryData.categoryBreakdown,
        valueAnalysis: inventoryData.valueAnalysis,
        preparationCosts: inventoryData.preparationCosts,
        timeline: inventoryData.timeline
      };
    }

    if (loadingAssets) return null;

    try {
      setLoadingAssets(true);

      const params = {
        skip: 0,
        limit: 5000,
        q: undefined,
        status: filters.status || undefined,
        category: filters.category || undefined,
        product_id: filters.productId || undefined,
        managed_by: filters.managedBy || undefined,
        start_date: filters.startDate ? new Date(filters.startDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
        end_date: filters.endDate ? new Date(filters.endDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
        min_value: filters.minValue ? parseFloat(filters.minValue) : undefined,
        max_value: filters.maxValue ? parseFloat(filters.maxValue) : undefined
      };

      const assets = await financeService.getAssets(params);
      const validAssets = Array.isArray(assets) ? assets : (assets?.data || []);
      const processedData = processInventoryData(validAssets, inventoryData.summary);

      setInventoryData(prev => ({
        ...prev,
        assets: validAssets,
        ...processedData
      }));

      return {
        assets: validAssets,
        ...processedData
      };
    } catch (err) {
      console.error('Erro ao carregar ativos para analytics:', err);
      showNotification('Erro ao carregar detalhes dos ativos', 'error');
      return null;
    } finally {
      setLoadingAssets(false);
    }
  };

  const processPreparationCosts = (assets) => {
    const monthlyData = {};
    assets.forEach(asset => {
      if (asset.preparation_costs && parseFloat(asset.preparation_costs) > 0) {
        const month = format(new Date(asset.created_at), 'yyyy-MM');
        if (!monthlyData[month]) {
          monthlyData[month] = { month, totalCosts: 0, count: 0 };
        }
        monthlyData[month].totalCosts += parseFloat(asset.preparation_costs || 0);
        monthlyData[month].count += 1;
      }
    });
    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  };

  const processTimeline = (assets) => {
    const timelineData = {};
    
    assets.forEach(asset => {
      const date = format(new Date(asset.created_at), 'yyyy-MM-dd');
      if (!timelineData[date]) {
        timelineData[date] = { 
          date, 
          created: 0, 
          completed: 0, 
          totalValue: 0,
          avgPreparationTime: 0 
        };
      }
      
      timelineData[date].created += 1;
      timelineData[date].totalValue += parseFloat(asset.purchase_value || 0);
      
      if (asset.status === 'entregue') {
        timelineData[date].completed += 1;
      }
      
      // Calcular tempo médio de preparação (se aplicável)
      if (asset.delivery_date && asset.created_at) {
        const preparationDays = Math.ceil(
          (new Date(asset.delivery_date) - new Date(asset.created_at)) / (1000 * 60 * 60 * 24)
        );
        timelineData[date].avgPreparationTime = preparationDays;
      }
    });

    return Object.values(timelineData).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getStatusLabel = (status) => {
    const labels = {
      'em_aquisicao': 'Em Aquisição',
      'em_preparacao': 'Em Preparação', 
      'pronto_sorteio': 'Pronto para Sorteio',
      'sorteado_aguardando': 'Sorteado (Aguardando)',
      'entregue': 'Entregue',
      'cancelado': 'Cancelado',
      // Mapeamentos alternativos para compatibilidade
      'planejado': 'Planejado',
      'preparado': 'Preparado'
    };
    return labels[status] || status;
  };

  const formatCurrency = (value) => {
    // Verificar se o valor é válido
    const numValue = parseFloat(value);
    if (isNaN(numValue) || !isFinite(numValue)) {
      return 'R$ 0,00';
    }
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue);
  };

  const handleApplyFilters = () => {
    loadInventoryData();
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: subDays(new Date(), 30),
      endDate: new Date(),
      status: '',
      category: '',
      productId: '',
      minValue: '',
      maxValue: '',
      managedBy: '',
      assetId: ''
    });
  };

  const renderSummaryCards = () => {
    const s = inventoryData.summary || {};
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Inventário atual */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <InventoryIcon color="primary" />
                <Typography variant="subtitle2">Inventário (Qtd)</Typography>
              </Stack>
              <Typography variant="h5">{s.total_assets || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <MonetizationOnIcon color="success" />
                <Typography variant="subtitle2">Patrimônio Atual</Typography>
              </Stack>
              <Typography variant="h5">{formatCurrency(s.total_value || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BuildCircleIcon color="warning" />
                <Typography variant="subtitle2">Custos Prep. (Atual)</Typography>
              </Stack>
              <Typography variant="h5">{formatCurrency(s.total_preparation_costs || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AssessmentIcon color="info" />
                <Typography variant="subtitle2">Invest. Inicial (Atual)</Typography>
              </Stack>
              <Typography variant="h5">{formatCurrency(s.total_initial_investment_current || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Histórico */}
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <InventoryIcon color="disabled" />
                <Typography variant="subtitle2">Total Histórico (Qtd)</Typography>
              </Stack>
              <Typography variant="h6">{s.total_assets_historical || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <MonetizationOnIcon color="disabled" />
                <Typography variant="subtitle2">Valor Total (Hist.)</Typography>
              </Stack>
              <Typography variant="h6">{formatCurrency(s.total_value_historical || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BuildCircleIcon color="disabled" />
                <Typography variant="subtitle2">Custos Prep. (Hist.)</Typography>
              </Stack>
              <Typography variant="h6">{formatCurrency(s.total_preparation_costs_historical || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AssessmentIcon color="disabled" />
                <Typography variant="subtitle2">Invest. Inicial (Hist.)</Typography>
              </Stack>
              <Typography variant="h6">{formatCurrency(s.total_initial_investment_historical || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const handleGeneratePDF = async () => {
    try {
      showLoading('Gerando relatório de inventário...');

      // Garantir que os ativos e gráficos detalhados foram carregados para o PDF
      const loaded = await ensureAssetsLoaded();

      const assetsForReport = loaded?.assets ?? inventoryData.assets;
      const chartsForReport = {
        statusDistribution: loaded?.statusDistribution ?? inventoryData.statusDistribution,
        categories: loaded?.categoryBreakdown ?? inventoryData.categoryBreakdown,
        valueAnalysis: loaded?.valueAnalysis ?? inventoryData.valueAnalysis,
        preparationCosts: loaded?.preparationCosts ?? inventoryData.preparationCosts,
        timeline: loaded?.timeline ?? inventoryData.timeline
      };
      
      const reportData = {
        title: 'Relatório Analítico de Inventário',
        period: `${format(filters.startDate, 'dd/MM/yyyy')} - ${format(filters.endDate, 'dd/MM/yyyy')}`,
        filters: {
          status: filters.status,
          category: filters.category,
          productId: filters.productId,
          minValue: filters.minValue,
          maxValue: filters.maxValue
        },
        summary: inventoryData.summary,
        assets: assetsForReport,
        charts: chartsForReport
      };

      await financeService.generateInventoryReport(reportData);
      showNotification('Relatório gerado com sucesso!', 'success');
    } catch (err) {
      showNotification('Erro ao gerar relatório', 'error');
      console.error('Erro ao gerar PDF:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Verificar permissões
  if (!hasPermission('analytics.inventory.read')) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Acesso negado. Você não tem permissão para visualizar análises de inventário.
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
                Analytics de Inventário
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Análise completa do controle de estoque e ativos
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadInventoryData}
                size="large"
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={handleGeneratePDF}
                size="large"
                color="secondary"
              >
                Baixar PDF
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Filtros de Análise
            </Typography>
          </Stack>
          
          <Grid container spacing={3}>
            {/* Primeira linha - Filtros principais */}
                  <Grid item xs={12} sm={6} md={2.4}>
                    <DatePicker
                    label="Data Inicial"
                    value={filters.startDate}
                    onChange={(newValue) => setFilters({ ...filters, startDate: newValue })}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={2.4}>
                    <DatePicker
                    label="Data Final"
                    value={filters.endDate}
                    onChange={(newValue) => setFilters({ ...filters, endDate: newValue })}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {enums.assetStatuses.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {getStatusLabel(status.value)}
                      </MenuItem>
                      ))}
                    </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                      label="Categoria"
                    >
                      <MenuItem value="">Todas</MenuItem>
                      <MenuItem value="Carros">Carros</MenuItem>
                      <MenuItem value="Motos">Motos</MenuItem>
                      <MenuItem value="Caminhões">Caminhões</MenuItem>
                      <MenuItem value="Imóveis">Imóveis</MenuItem>
                      <MenuItem value="Acessórios">Acessórios</MenuItem>
                      <MenuItem value="Eletrônicos">Eletrônicos</MenuItem>
                      <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                      <MenuItem value="Materiais">Materiais</MenuItem>
                      <MenuItem value="Móveis">Móveis</MenuItem>
                      <MenuItem value="Consumíveis">Consumíveis</MenuItem>
                      <MenuItem value="Ferramentas">Ferramentas</MenuItem>
                      <MenuItem value="Peças">Peças</MenuItem>
                      <MenuItem value="Objetos">Objetos</MenuItem>
                    </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <AsyncSearchSelect
                      label="Produto/Sorteio"
                      value={filters.productId}
                      selectedLabel={productFilterLabel}
                      onChange={(id, option) => {
                        const nextId = String(id ?? '').trim();
                        setFilters(prev => ({ ...prev, productId: nextId }));
                        setProductFilterLabel(option?.label || '');
                      }}
                      fetchPage={({ q, offset, limit }) => financeService.searchProducts({ q, offset, limit })}
                      placeholder="Buscar produto..."
                    />
                  </Grid>

                  {/* Segunda linha - Filtros avançados */}
            <Grid item xs={12} sm={6} md={2.4}>
              <AsyncSearchSelect
                label="Ativo Específico"
                value={filters.assetId}
                onChange={(id) => setFilters({ ...filters, assetId: id })}
                fetchPage={({ q, offset, limit }) => financeService.searchAssets({ q, offset, limit })}
                placeholder="Buscar ativo..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Gerenciado por</InputLabel>
                <Select
                  value={filters.managedBy}
                  onChange={(e) => setFilters({ ...filters, managedBy: e.target.value })}
                  label="Gerenciado por"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name || user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Valor Mínimo"
                type="number"
                size="small"
                value={filters.minValue}
                onChange={(e) => setFilters({ ...filters, minValue: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Valor Máximo"
                type="number"
                size="small"
                value={filters.maxValue}
                onChange={(e) => setFilters({ ...filters, maxValue: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  size="small"
                  sx={{ minHeight: 40 }}
                >
                  Aplicar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  size="small"
                  sx={{ minHeight: 40 }}
                >
                  Limpar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Cards de Resumo */}
        {inventoryData.summary && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={600}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                        <InventoryIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Total de Ativos
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary.main">
                          {inventoryData.summary.total_assets || 0}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={800}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'success.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                        <MonetizationOnIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Valor do Patrimônio
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {formatCurrency(inventoryData.summary.total_value || 0)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1000}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'warning.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                        <BuildCircleIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Ativos em Preparação
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                          {inventoryData.summary.in_preparation || 0}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1200}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'info.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                        <AssessmentIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          % Entregas Concluídas
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="info.main">
                          {((inventoryData.summary.completion_rate || 0) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          </Grid>
        )}

        {/* Tabs de Análises */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={currentTab} 
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons="auto"
            >
              <Tab 
                icon={<DonutSmallIcon />} 
                label="Status" 
                iconPosition="start"
              />
              <Tab 
                icon={<CategoryIcon />} 
                label="Categorias" 
                iconPosition="start"
              />
              <Tab 
                icon={<BarChartIcon />} 
                label="Concentração de Valor" 
                iconPosition="start"
              />
              <Tab 
                icon={<ShowChartIcon />} 
                label="Custos de Preparação" 
                iconPosition="start"
              />
              <Tab 
                icon={<TimelineIcon />} 
                label="Timeline" 
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Conteúdo das Tabs */}
          <Box sx={{ p: 3 }}>
            {/* Tab 0 - Status */}
            {currentTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Distribuição por Status
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ height: 300, mt: 2 }}>
                      {inventoryData.statusDistribution && inventoryData.statusDistribution.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={inventoryData.statusDistribution}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${getStatusLabel(name)} ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="count"
                              nameKey="status"
                            >
                              {inventoryData.statusDistribution.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%',
                          color: 'text.secondary' 
                        }}>
                          <Typography variant="body1">Nenhum dado disponível para exibir</Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Detalhes por Status
                      </Typography>
                      <Stack spacing={2}>
                        {inventoryData.statusDistribution && inventoryData.statusDistribution.length > 0 ? (
                          inventoryData.statusDistribution.map((status, index) => (
                            <Card key={index} sx={{ p: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body1" fontWeight="bold">
                                    {status.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {status.count} ativos • {formatCurrency(status.value)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: status.color
                                  }}
                                />
                              </Stack>
                            </Card>
                          ))
                        ) : (
                          <Card sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                              Nenhum status disponível
                            </Typography>
                          </Card>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tab 1 - Categorias */}
            {currentTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Análise por Categoria
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={inventoryData.categoryBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `${value}`}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip 
                        formatter={(value, name) => [
                          name === 'count' ? `${value} ativos` : formatCurrency(value),
                          name === 'count' ? 'Quantidade' : 'Valor Total'
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="count" name="Quantidade">
                        {inventoryData.categoryBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                      <Line yAxisId="right" type="monotone" dataKey="totalValue" stroke="#f44336" name="Valor Total" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}

            {/* Tab 2 - Análise de Valor */}
            {currentTab === 2 && (
              <Box>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Análise de Concentração de Valor
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Visualiza como os ativos estão distribuídos por faixas de valor, ajudando a identificar 
                      a concentração do patrimônio e riscos associados ao portfólio de investimentos.
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                      <Card sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Distribuição por Faixa de Valor
                        </Typography>
                        <Box sx={{ height: 350, mt: 2 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={inventoryData.valueAnalysis}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="label" 
                                tick={{ fontSize: 12 }}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis 
                                tick={{ fontSize: 12 }}
                              />
                              <RechartsTooltip 
                                formatter={(value, name) => [
                                  name === 'count' ? `${value} ativos` : value,
                                  name === 'count' ? 'Quantidade' : 'Quantidade'
                                ]}
                              />
                              <Legend />
                              <Bar dataKey="count" name="Quantidade">
                                {inventoryData.valueAnalysis.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Card>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <Stack spacing={2}>
                        <Card sx={{ p: 2 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Resumo da Distribuição
                          </Typography>
                          <Stack spacing={2}>
                            {inventoryData.valueAnalysis && inventoryData.valueAnalysis.map((range, index) => (
                              <Box key={index}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {range.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {range.count} ativos ({range.count > 0 && inventoryData.assets ? 
                                        ((range.count / inventoryData.assets.length) * 100).toFixed(1) : 0}%)
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight="bold" color="success.main">
                                    {formatCurrency(range.totalValue)}
                                  </Typography>
                                </Stack>
                                <Divider sx={{ mt: 1 }} />
                              </Box>
                            ))}
                          </Stack>
                        </Card>

                        <Card sx={{ p: 2, bgcolor: 'info.light', color: 'info.dark' }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            💡 Insights
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            • <strong>Alta concentração</strong> em faixas maiores indica portfólio de alto valor<br/>
                            • <strong>Distribuição equilibrada</strong> reduz riscos de concentração<br/>
                            • <strong>Muitos ativos</strong> em faixas baixas podem indicar necessidade de otimização
                          </Typography>
                        </Card>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
            )}

            {/* Tab 3 - Custos de Preparação */}
            {currentTab === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Evolução dos Custos de Preparação
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={inventoryData.preparationCosts}>
                      <defs>
                        <linearGradient id="colorCosts" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ff9800" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ff9800" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <RechartsTooltip 
                        formatter={(value) => [formatCurrency(value), 'Custos']}
                        labelFormatter={(label) => `Mês: ${label}`}
                      />
                      <Area
                        type="monotone"
                        dataKey="totalCosts"
                        stroke="#ff9800"
                        fillOpacity={1}
                        fill="url(#colorCosts)"
                        name="Custos de Preparação"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}

            {/* Tab 4 - Timeline */}
            {currentTab === 4 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Timeline de Criação e Conclusão
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={inventoryData.timeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="created" fill="#2196f3" name="Criados" />
                      <Bar yAxisId="left" dataKey="completed" fill="#4caf50" name="Concluídos" />
                      <Line yAxisId="right" type="monotone" dataKey="totalValue" stroke="#f44336" name="Valor Total" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default InventoryAnalytics;